import React, {useState} from 'react';

import {DataGridPro, GridColDef} from '@mui/x-data-grid-pro';
import Dialog, {DialogContent} from 'components/Dialog';
import {useTranslation} from 'react-i18next';
import TextField from 'components/inputs/TextField';
import Button from 'components/Button';
import {DialogActions, Grid} from '@mui/material';
import {useGetApi} from 'common/hooks/useGetApi';
import api from 'api';
import {useModal} from 'common/hooks/useModal';
import {SetValueOptions} from 'components/hook-form/helpers';
import {useFormContext} from 'react-hook-form';
import {
   IIPAccessRulesSettings,
   IPAccessRulesSettingsNames,
} from 'types/diplomat/sftp/IPAccessRulesSettings';
import {ReactFChildrenProps} from 'types/react/ReactFChildrenProps';

interface LookUpListDialogProps {
   open: boolean;
   onClose: (selectedIps: string[] | null) => void; // return null if canceled
}

type IpData = {id: string; ip: string};

export const LookUpListButton = () => {
   const {t} = useTranslation();
   const {open, handleOpen, handleClose} = useModal(false);
   const {watch, setValue} = useFormContext<IIPAccessRulesSettings>();

   return (
      <>
         <Button onClick={handleOpen}>{t('LookUp')}</Button>
         {open && (
            <LookUpListDialog
               open={true}
               onClose={(ips) => {
                  handleClose();
                  if (ips) {
                     setValue(
                        IPAccessRulesSettingsNames.exceptions,
                        [...watch(IPAccessRulesSettingsNames.exceptions)!, ...ips],
                        SetValueOptions
                     );
                  }
               }}
            />
         )}
      </>
   );
};

export const LookUpListDialog: ReactFChildrenProps<LookUpListDialogProps> = ({open, onClose}) => {
   const {t} = useTranslation();

   const [days, setDays] = useState<number>(30);
   const [selectionModel, setSelectionModel] = useState<string[]>([]);

   // Columns definition for DataGridPro
   const columns: GridColDef[] = [{field: 'ip', headerName: 'IP Address', flex: 1}];

   const {request, state: ips, loading} = useGetApi<string[], IpData[]>(api.authenticatedips, {
      transformData(data) {
         setSelectionModel(data); // select all by default
         return data.map((ip) => ({ip, id: ip}));
      },
   });

   // Close dialog
   const handleClose = (save: boolean) => {
      if (save && ips) {
         const selectedIps = ips
            .filter((row) => selectionModel.includes(row.id))
            .map((row) => row.ip);
         onClose(selectedIps);
      } else {
         onClose(null);
      }
   };

   return (
      <Dialog
         open={open}
         close={() => handleClose(false)}
         fullWidth
         maxWidth="md"
         header={t('Look Up IP List')}
      >
         <DialogContent>
            <Grid container spacing={3}>
               <Grid item xs={3}>
                  {/* Input field for days */}
                  <TextField
                     type="number"
                     label="Days"
                     value={days}
                     onChange={(e) => setDays(Number(e.target.value))}
                     inputProps={{min: 1, max: 9999}}
                     fullWidth
                     // margin="normal"
                  />
               </Grid>
               <Grid item xs={6}>
                  {/* Fetch button */}
                  <Button
                     variant="contained"
                     onClick={() => {
                        setSelectionModel([]);
                        request(`?days=${days}`);
                     }}
                     disabled={loading || days < 1 || days > 9999}
                     style={{marginTop: '1rem'}}
                  >
                     {loading ? 'Loading...' : 'Fetch IPs'}
                  </Button>
               </Grid>
            </Grid>

            {/* DataGridPro table with checkbox selection */}
            <div style={{height: 400, width: '100%'}}>
               <DataGridPro
                  rows={ips || []}
                  columns={columns}
                  loading={loading}
                  checkboxSelection
                  //  disableRowSelectionOnClick
                  disableSelectionOnClick
                  selectionModel={selectionModel}
                  onSelectionModelChange={(newSelection) =>
                     setSelectionModel(newSelection as string[])
                  }
               />
            </div>
         </DialogContent>

         <DialogActions>
            <Button
               onClick={() => handleClose(true)}
               variant="contained"
               disabled={selectionModel.length === 0}
            >
               Save
            </Button>
            <Button onClick={() => handleClose(false)}>Cancel</Button>
         </DialogActions>
      </Dialog>
   );
};
